import { Component, DoCheck, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AppService } from '../../../../services/app.service';
import { AvatarPrimitive } from '../../../../models/app/avatar-primitive';
import { ChipSelectorComponent } from '../../chip-selector/chip-selector.component';
import { ActingProfileCharacterType } from '../../../../models/data/talent-core/acting-profile-character-type';
import { ActingProfileEthnicity } from '../../../../models/data/talent-core/acting-profile-ethnicity';
import { Gender } from '../../../../models/data/talent-core/gender';
import { Ethnicity } from '../../../../models/data/talent-core/ethnicity';
import { CharacterType } from '../../../../models/data/talent-core/character-type';
import { ActingProfileEnvelope } from '../../../../models/net/envelope/acting-profile-envelope';
import { ActingProfileDocument } from '../../../../models/net/envelope/documents/acting-profile-document';
import { EmbeddableTileComponent } from '../../../../interfaces/embeddable-tile-component';
import { ComponentDialogComponent } from '../../../component-dialog/component-dialog.component';

@Component({
    selector: 'app-acting-profile-tile',
    templateUrl: './acting-profile-tile.component.html',
    styleUrls: ['./acting-profile-tile.component.css', '../../../../app.component.css']
})

export class ActingProfileTileComponent implements OnInit, DoCheck, EmbeddableTileComponent {

    public availableResources: AvatarPrimitive[] = [];
    public availableGenders: Gender[] = [];
    public availableCharacterTypes: AvatarPrimitive[] = [];
    public availableEthnicities: AvatarPrimitive[] = [];
    public selectedResourceIds: number[] = [];
    public selectedCharacterTypeIds: number[] = [];
    public selectedEthnicityIds: number[] = [];
    public itemEnvelopeSnapshot: ActingProfileEnvelope | null = null;
    public isPendingChanges: boolean = false;
    public componentDialogComponent?: ComponentDialogComponent;

    @Input() public itemEnvelope: ActingProfileEnvelope | undefined;

    @ViewChild("characterTypeChipSelector") characterTypeChipSelector!: ChipSelectorComponent;
    @ViewChild("ethnicitySelector") ethnicitySelector!: ChipSelectorComponent;

    @Output() pendingChangesStateChanged = new EventEmitter<boolean>();
    @Output() setAcceptButtonCaption = new EventEmitter<string>();
    @Output() setCloseButtonCaption = new EventEmitter<string>();
    @Output() closeContainerRequest = new EventEmitter();
    @Output() setIsDataSourceAltered = new EventEmitter<boolean>();

    public constructor(public appService: AppService) { }

    public setDataSource = (dataSource: any, componentDialogComponent: ComponentDialogComponent): void => {

        this.componentDialogComponent = componentDialogComponent;
        this.itemEnvelope = dataSource as ActingProfileEnvelope;
        this.itemEnvelopeSnapshot = this.appService.cloneObject(this.itemEnvelope);

        this.availableGenders = (this.itemEnvelope as ActingProfileEnvelope).Genders;

        const availableCharacterTypes: AvatarPrimitive[] = (this.itemEnvelope as ActingProfileEnvelope).CharacterTypes.map((i: CharacterType) => {
            let result = new AvatarPrimitive();
            result.Id = i.CharacterTypeId;
            result.Name = i.UID;
            result.AvatarBlobPath = this.appService.defaultImageUri;
            return result;
        });

        const availableEthnicities: AvatarPrimitive[] = (this.itemEnvelope as ActingProfileEnvelope).Ethnicities.map((i: Ethnicity) => {
            let result = new AvatarPrimitive();
            result.Id = i.EthnicityId;
            result.Name = i.UID;
            result.AvatarBlobPath = this.appService.defaultImageUri;
            return result;
        });

        this.availableCharacterTypes = availableCharacterTypes;
        this.availableEthnicities = availableEthnicities;

        const actingProfileId: number = (this.itemEnvelope?.Document as ActingProfileDocument).Item.ActingProfileId;

        this.selectedCharacterTypeIds = (this.itemEnvelope?.Document as ActingProfileDocument).CharacterTypes.filter((i: ActingProfileCharacterType) => i.ActingProfileId == actingProfileId).map(i => i.CharacterTypeId);
        this.selectedEthnicityIds = (this.itemEnvelope?.Document as ActingProfileDocument).Ethnicities.filter((i: ActingProfileEthnicity) => i.ActingProfileId == actingProfileId).map(i => i.EthnicityId);
    }

    public ngOnInit(): void { }

    public ngAfterViewInit(): void {

        this.characterTypeChipSelector.itemAdded.subscribe((item: AvatarPrimitive) => {

            this.addCharacterTypeToActingProfile(item.Id);
        });

        this.characterTypeChipSelector.itemRemoved.subscribe((item: AvatarPrimitive) => {

            this.removeCharacterTypeFromActingProfile(item.Id);
        });

        this.ethnicitySelector.itemAdded.subscribe((item: AvatarPrimitive) => {

            this.addEthnicityToActingProfile(item.Id);
        });

        this.ethnicitySelector.itemRemoved.subscribe((item: AvatarPrimitive) => {

            this.removeEthnicityFromActingProfile(item.Id);
        });
    }

    public addCharacterTypeToActingProfile = (characterTypeId: number): void => {

        const newActingProfileCharacterType: ActingProfileCharacterType = new ActingProfileCharacterType();

        if (this.itemEnvelope?.Document) {

            newActingProfileCharacterType.ActingProfileId = (this.itemEnvelope.Document as ActingProfileDocument).Item.ActingProfileId;
            newActingProfileCharacterType.CharacterTypeId = characterTypeId;

            (this.itemEnvelope.Document as ActingProfileDocument).CharacterTypes.push(newActingProfileCharacterType);
        }
    }

    public removeCharacterTypeFromActingProfile = (characterTypeId: number): void => {

        if (this.itemEnvelope?.Document) {

            const document: ActingProfileDocument = this.itemEnvelope.Document as ActingProfileDocument;
            const actingProfileId: number = document.Item.ActingProfileId;
            const actingProfileCharacterTypes: ActingProfileCharacterType[] = (this.itemEnvelope?.Document as ActingProfileDocument).CharacterTypes.filter((i: ActingProfileCharacterType) => i.ActingProfileId == actingProfileId && i.CharacterTypeId == characterTypeId);

            const itemIndex: number | undefined = document.CharacterTypes.map((i: ActingProfileCharacterType) => i.ActingProfileCharacterTypeId).indexOf(actingProfileCharacterTypes[0].ActingProfileCharacterTypeId, 0);

            if (itemIndex != undefined && itemIndex > -1) {

                document.CharacterTypes.splice(itemIndex, 1);
            }
        }
    }

    public addEthnicityToActingProfile = (ethnicityId: number): void => {

        const newActingProfileEthnicity: ActingProfileEthnicity = new ActingProfileEthnicity();

        if (this.itemEnvelope?.Document) {

            newActingProfileEthnicity.ActingProfileId = (this.itemEnvelope.Document as ActingProfileDocument).Item.ActingProfileId;
            newActingProfileEthnicity.EthnicityId = ethnicityId;

            (this.itemEnvelope.Document as ActingProfileDocument).Ethnicities.push(newActingProfileEthnicity);
        }
    }

    public removeEthnicityFromActingProfile = (ethnicityId: number): void => {

        if (this.itemEnvelope?.Document) {

            const document: ActingProfileDocument = this.itemEnvelope.Document as ActingProfileDocument;
            const actingProfileId: number = document.Item.ActingProfileId;
            const actingProfileEthnicitys: ActingProfileEthnicity[] = (this.itemEnvelope?.Document as ActingProfileDocument).Ethnicities.filter((i: ActingProfileEthnicity) => i.ActingProfileId == actingProfileId && i.EthnicityId == ethnicityId);

            const itemIndex: number | undefined = document.Ethnicities.map((i: ActingProfileEthnicity) => i.ActingProfileEthnicityId).indexOf(actingProfileEthnicitys[0].ActingProfileEthnicityId, 0);

            if (itemIndex != undefined && itemIndex > -1) {

                document.Ethnicities.splice(itemIndex, 1);
            }
        }
    }

    public ngDoCheck(): void {

        const isPendingChanges = this.isPendingChanges;

        if (JSON.stringify(this.itemEnvelope) != JSON.stringify(this.itemEnvelopeSnapshot)) {

            this.isPendingChanges = true;

        } else {

            this.isPendingChanges = false;
        }

        if (this.isPendingChanges != isPendingChanges) {

            this.pendingChangesStateChanged.emit(this.isPendingChanges);
        }
    }

    public validateBeforeAccept(): boolean {

        let result: boolean = true;

        return result;
    }
}
